import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import type { MatchView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';
import { appDateTime } from '@/utils/appDateFormat';
import { isPlayedMatchStatus } from '@/utils/tournamentPostCompletion';

interface DeleteMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: (matchId: number) => Promise<void> | void;
  match: MatchView | null;
}

const DeleteMatchModal: React.FC<DeleteMatchModalProps> = ({ isOpen, onClose, onDelete, match }) => {
  const { t } = useTranslation('tournament');
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  // CO-921 (owner 2026-10-21): deleting a played match removes its officials' work points, and any
  // amount already paid for it is clawed back on the next payroll. Say so before HR confirms.
  const isPlayed = !!match && isPlayedMatchStatus(match.status);
  const pointHolders = (match?.refereeTeamMembers ?? []).filter((m) => (m.workPoints ?? 0) > 0);

  const handleDelete = async () => {
    if (!match) return;
    setIsDeleting(true);
    try {
      await onDelete(match.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDateTime = (dateTimeString: string): string => {
    const date = parseApiWallClock(dateTimeString) ?? new Date(dateTimeString);
    return appDateTime(date);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="sm" data-testid="delete-match-modal">
        {/* Header */}
        <DialogHeader className="py-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center me-3 flex-shrink-0">
              <Icon name="Trash2" size={20} className="text-red-600" />
            </div>
            <div>
              <DialogTitle className="font-bold">{t('Delete Match')}</DialogTitle>
              <DialogDescription>{t('This action cannot be undone')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <DialogBody>
          <div className="mb-4">
            <p className="text-foreground mb-4">
              {t('Are you sure you want to delete this match?')}
            </p>
            {match && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center text-sm">
                  <Icon name="Hash" size={14} className="text-gray-400 me-2" />
                  <span className="font-medium text-foreground">{t('Match ID:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-foreground">{match?.id}</span>
                </div>
                <div className="flex items-start text-sm">
                  <Icon name="Trophy" size={14} className="text-gray-400 me-2 mt-0.5" />
                  <span className="font-medium text-foreground">{t('Opponents:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-foreground">{match?.team1Name} {t('vs')} {match?.team2Name}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Icon name="MapPin" size={14} className="text-gray-400 me-2" />
                  <span className="font-medium text-foreground">{t('Location:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-foreground">{match?.location}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Icon name="Calendar" size={14} className="text-gray-400 me-2" />
                  <span className="font-medium text-foreground">{t('Date & Time:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-gray-900">{formatDateTime(match?.scheduledDateTime ?? '')}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Icon name="Users" size={14} className="text-gray-400 me-2" />
                  <span className="font-medium text-foreground">{t('Match Officials:', { nsSeparator: false })}</span>
                  <span className="ms-2 text-foreground">{match?.refereeTeamMembers?.length} {t('members')}</span>
                </div>
              </div>
            )}
          </div>

          {isPlayed && (
            <div
              className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20"
              data-testid="delete-match-points-notice"
            >
              <div className="flex items-start gap-2">
                <Icon name="Coins" size={18} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="min-w-0 space-y-1.5">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">{t('wpFollow.delete.title')}</p>
                  <p className="text-sm text-amber-800 dark:text-amber-300">{t('wpFollow.delete.body')}</p>
                  {pointHolders.length > 0 && (
                    <ul className="space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
                      {pointHolders.map((m) => (
                        <li key={m.id} className="flex items-center gap-1.5">
                          <Icon name="UserMinus" size={12} className="shrink-0" />
                          <span className="truncate">{t('wpFollow.delete.memberLoses', { name: m.userName, points: m.workPoints })}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
            <Icon name="AlertTriangle" size={18} className="text-red-600 me-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">
              {t('Deleting this match will permanently remove all associated data including team member assignments and match history.')}
            </p>
          </div>
        </DialogBody>

        {/* Footer */}
        <DialogFooter className="py-4 bg-muted/50 rounded-b-xl">
          <Button
            onClick={onClose}
            className="px-6 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
            data-testid="delete-match-cancel-btn"
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-6 py-2 rounded-lg flex items-center"
            data-testid="delete-match-confirm-btn"
          >
            <Icon name="Trash2" size={16} className="me-2" />
            {isDeleting ? t('Deleting...') : isPlayed ? t('wpFollow.delete.confirm') : t('Delete Match')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteMatchModal;
